var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import isEqual from "lodash/isEqual";
import { isPublisher } from "../utils/permissionsUtils";
import { useDateInputValidity } from "./useDateInputValidity";
import { usePermissions } from "./usePermissions";
import { usePublisherAutoFill } from "./usePublisherAutoFill";
import { appendDateRangeParams, areDateRangesValid, DATE_RANGE_FILTER_PARAMS, dateRangeFiltersFromParams, EMPTY_DATE_RANGE_FILTERS, hasActiveDateRangeFilter, pickDateRangeFilters, } from "../utils/dateRangeFilters";
// String-valued filters that map 1:1 onto text URL params.
var TITLE_FILTER_URL_PARAMS = [
    "id",
    "name",
    "contributor",
    "publisher",
    "productNumber",
    "collection",
];
// All filter URL params, including the boolean "hasCommentForAgency" toggle and the date ranges.
var ALL_TITLE_FILTER_URL_PARAMS = __spreadArray(__spreadArray(__spreadArray([], TITLE_FILTER_URL_PARAMS, true), [
    "hasCommentForAgency"
], false), DATE_RANGE_FILTER_PARAMS, true);
export var hasTitleFilterUrlParams = function (search) {
    var urlSearchParams = new URLSearchParams(search);
    return ALL_TITLE_FILTER_URL_PARAMS.some(function (key) { return urlSearchParams.has(key); });
};
var INITIAL_TITLE_FILTERS = __assign({ id: "", name: "", contributor: "", publisher: "", productNumber: "", collection: "", hasCommentForAgency: false }, EMPTY_DATE_RANGE_FILTERS);
/**
 * The applied filters as the URL holds them.
 *
 * @param search `location.search` of the list
 * @param publicOnlyView the agency note filter is never applied in the public-only view
 */
var filtersFromSearch = function (search, publicOnlyView) {
    var qs = new URLSearchParams(search);
    return __assign({ id: qs.get("id") || "", name: qs.get("name") || "", contributor: qs.get("contributor") || "", publisher: qs.get("publisher") || "", productNumber: qs.get("productNumber") || "", collection: qs.get("collection") || "", hasCommentForAgency: !publicOnlyView && qs.get("hasCommentForAgency") === "true" }, dateRangeFiltersFromParams(qs));
};
export var withPublisherUrlParam = function (search, publisherSourceId) {
    var urlSearchParams = new URLSearchParams(search);
    urlSearchParams.set("publisher", publisherSourceId);
    return urlSearchParams.toString();
};
export var useTitleFilters = function () {
    var history = useHistory();
    var location = useLocation();
    // The agency note filter is agency-internal: the public-only view (e.g.
    // Distributor) never gets it, not even from a pasted URL (DB-837).
    // Roles that could not be loaded are treated the same way.
    var _a = usePermissions(), searchAsPublicOnly = _a.searchAsPublicOnly, activeRoles = _a.activeRoles, activeRolesError = _a.activeRolesError;
    var initial = INITIAL_TITLE_FILTERS;
    // Initialised from the URL in the first render: filters patched in by an
    // effect afterwards would change the search and send it twice.
    var _b = useState(function () {
        return filtersFromSearch(location.search, searchAsPublicOnly);
    }), filters = _b[0], setFilters = _b[1];
    var _c = useState(filters.id), temporaryIdFilter = _c[0], setTemporaryIdFilter = _c[1];
    var _d = useState(filters.name), temporaryNameFilter = _d[0], setTemporaryNameFilter = _d[1];
    var _e = useState(filters.contributor), temporaryContributorFilter = _e[0], setTemporaryContributorFilter = _e[1];
    var _f = useState(filters.publisher), temporaryPublisherFilter = _f[0], setTemporaryPublisherFilter = _f[1];
    var _g = useState(filters.productNumber), temporaryProductNumberFilter = _g[0], setTemporaryProductNumberFilter = _g[1];
    var _h = useState(""), publisherSearchQuery = _h[0], setPublisherSearchQuery = _h[1];
    var _j = useState(function () { return pickDateRangeFilters(filters); }), temporaryDateRangeFilters = _j[0], setTemporaryDateRangeFilters = _j[1];
    var dateInputs = useDateInputValidity();
    var resetDateInputs = dateInputs.resetInputs;
    useEffect(function () {
        var f = filtersFromSearch(location.search, searchAsPublicOnly);
        setFilters(function (prev) { return (isEqual(prev, f) ? prev : f); });
        setTemporaryIdFilter(f.id);
        setTemporaryNameFilter(f.name);
        setTemporaryContributorFilter(f.contributor);
        setTemporaryPublisherFilter(f.publisher);
        setTemporaryProductNumberFilter(f.productNumber);
        setTemporaryDateRangeFilters(pickDateRangeFilters(f));
        resetDateInputs();
    }, [location.search, resetDateInputs, searchAsPublicOnly]);
    // A list opened without filter params shows a publisher user their own titles.
    var autoFill = usePublisherAutoFill({
        appliesTo: isPublisher,
        hasFilterParams: hasTitleFilterUrlParams,
        canAutoFill: function () { return !temporaryPublisherFilter; },
        onAutoFill: function (publisherSourceId) {
            setFilters(__assign(__assign({}, INITIAL_TITLE_FILTERS), { publisher: publisherSourceId }));
            setTemporaryPublisherFilter(publisherSourceId);
            history.push({
                search: withPublisherUrlParam(history.location.search, publisherSourceId),
            });
        },
    });
    /** False until the publisher auto-fill is decided — a search before it would be thrown away. */
    var filtersReady = !autoFill.isPending &&
        // the agency-only note filter from the URL waits for the roles that decide it
        !(activeRoles === undefined &&
            !activeRolesError &&
            new URLSearchParams(location.search).has("hasCommentForAgency"));
    var updateUrl = useCallback(function (f) {
        var params = new URLSearchParams();
        TITLE_FILTER_URL_PARAMS.forEach(function (key) {
            if (f[key])
                params.set(key, f[key]);
        });
        if (f.hasCommentForAgency)
            params.set("hasCommentForAgency", "true");
        appendDateRangeParams(params, f);
        history.push({ search: params.toString() });
    }, [history]);
    // Od after Do, or a picker holding text that is not a valid date.
    var hasInvalidDateRange = !areDateRangesValid(temporaryDateRangeFilters) ||
        dateInputs.hasInvalidInput;
    var setTemporaryDateRangeFilter = function (key, value) { return setTemporaryDateRangeFilters(function (prev) {
        var _a;
        return (__assign(__assign({}, prev), (_a = {}, _a[key] = value, _a)));
    }); };
    var handleApplyFilters = function () {
        // An Od after Do is reported in the filter pane and never sent.
        if (hasInvalidDateRange)
            return;
        var newFilters = __assign({ id: temporaryIdFilter.trim(), name: temporaryNameFilter, contributor: temporaryContributorFilter, publisher: temporaryPublisherFilter, productNumber: temporaryProductNumberFilter, collection: filters.collection, hasCommentForAgency: filters.hasCommentForAgency }, temporaryDateRangeFilters);
        autoFill.cancel();
        setFilters(newFilters);
        updateUrl(newFilters);
    };
    var handleClearCollectionFilter = function () {
        var newFilters = __assign(__assign({}, filters), { collection: "" });
        autoFill.cancel();
        setFilters(newFilters);
        updateUrl(newFilters);
    };
    var handleToggleCommentForAgencyFilter = function () {
        if (searchAsPublicOnly)
            return;
        var newFilters = __assign(__assign({}, filters), { hasCommentForAgency: !filters.hasCommentForAgency });
        autoFill.cancel();
        setFilters(newFilters);
        updateUrl(newFilters);
    };
    var handleClearFilters = function () {
        setTemporaryIdFilter("");
        setTemporaryNameFilter("");
        setTemporaryContributorFilter("");
        setTemporaryPublisherFilter("");
        setTemporaryProductNumberFilter("");
        setTemporaryDateRangeFilters(EMPTY_DATE_RANGE_FILTERS);
        resetDateInputs();
        autoFill.cancel();
        setFilters(initial);
        history.push({ search: "" });
    };
    var handlePublisherSearchInput = function (searchQuery) {
        setPublisherSearchQuery(searchQuery);
    };
    var handlePublisherSelection = function (id) {
        setTemporaryPublisherFilter(id);
    };
    var isAnyFilterActive = function () {
        return Boolean(filters.id ||
            filters.name ||
            filters.contributor ||
            filters.publisher ||
            filters.productNumber ||
            filters.collection ||
            filters.hasCommentForAgency ||
            hasActiveDateRangeFilter(filters));
    };
    return {
        filters: filters,
        filtersReady: filtersReady,
        /** true when the logged-in user or their roles could not be loaded */
        currentUserLoadFailed: autoFill.isError || activeRolesError,
        temporaryIdFilter: temporaryIdFilter,
        temporaryNameFilter: temporaryNameFilter,
        temporaryContributorFilter: temporaryContributorFilter,
        temporaryPublisherFilter: temporaryPublisherFilter,
        temporaryProductNumberFilter: temporaryProductNumberFilter,
        temporaryDateRangeFilters: temporaryDateRangeFilters,
        hasInvalidDateRange: hasInvalidDateRange,
        dateInputs: dateInputs,
        publisherSearchQuery: publisherSearchQuery,
        setTemporaryIdFilter: setTemporaryIdFilter,
        setTemporaryNameFilter: setTemporaryNameFilter,
        setTemporaryContributorFilter: setTemporaryContributorFilter,
        setTemporaryPublisherFilter: setTemporaryPublisherFilter,
        setTemporaryProductNumberFilter: setTemporaryProductNumberFilter,
        setTemporaryDateRangeFilter: setTemporaryDateRangeFilter,
        handlePublisherSearchInput: handlePublisherSearchInput,
        handlePublisherSelection: handlePublisherSelection,
        handleApplyFilters: handleApplyFilters,
        handleClearFilters: handleClearFilters,
        handleClearCollectionFilter: handleClearCollectionFilter,
        handleToggleCommentForAgencyFilter: handleToggleCommentForAgencyFilter,
        isAnyFilterActive: isAnyFilterActive,
    };
};
